import {
  Button,
  Checkbox,
  cn,
  FormField,
  FormFieldError,
  FormFieldHint,
  FormFieldLabel,
  Input,
} from '@eureca/ui';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import { BlockedEditTooltip } from '@/features/jobs/job-shared/BlockedEditTooltip';
import { PlusIcon } from './icons';
import { JobHeaderLogo } from '@/features/jobs/job-shared/JobHeaderLogo';

export interface SelectedSkill {
  id: string;
  /** Original preset name OR free-typed name. */
  name: string;
  /** Proficiency level id (basico/intermediario/avancado). */
  level: string;
  /** Pre-rendered display label, e.g. "Inglês - Avançado". */
  label: string;
}

export type DiversityCriterionId =
  | 'mulheres'
  | 'mulheres-negras'
  | 'transexuais'
  | 'lgbtqiap'
  | 'amarelas'
  | 'indigenas'
  | 'pardas'
  | 'pretas'
  | 'pessoas-com-deficiencia'
  | '50-plus';

export interface SelectedDisabilityCriterion {
  /** Subset of DISABILITY_OPTIONS ids that are selected. */
  selectedIds: string[];
  /** When 'outra' is selected, holds the free-typed text. */
  otherText: string;
  /** Pre-rendered display label, e.g. "Pessoas com deficiência - Auditiva e Física". */
  label: string;
}

export interface JobEditRequirementsValues {
  ageMin: string;
  ageMax: string;
  selectedCompetencias: SelectedSkill[];
  selectedDiferenciais: SelectedSkill[];
  selectedDiversity: DiversityCriterionId[];
  disabilityDetails: SelectedDisabilityCriterion | null;
}

export interface JobEditRequirementsPageProps {
  jobName: string;
  companyLogoUrl?: string | null;
  companyName?: string | null;
  values: JobEditRequirementsValues;
  errors: Record<string, string>;
  /** `true` enquanto há um PATCH em voo (botão Salvar mostra loading). */
  isSubmitting: boolean;
  /**
   * `false` quando há erro ativo de validação de idade (RN-02/RN-03 ou
   * `ageMax ≤ ageMin`). Desabilita o botão Salvar para impedir que o
   * consultor persista um estado inválido — bug reportado pela QA em que
   * as mensagens apareciam mas o save passava silenciosamente.
   */
  canSubmit: boolean;
  /** EU-569 — vaga Encerrada / Cancelada: modo somente visualização. */
  readOnly?: boolean;
  competenciaPresets: readonly string[];
  diferencialPresets: readonly string[];
  /**
   * Mensagem inline de cap quando o consultor tenta adicionar a 51ª
   * competência (FR-092). `undefined` enquanto não atingido.
   */
  competenciaCapError?: string;
  diferencialCapError?: string;
  onAgeChange: (field: 'ageMin' | 'ageMax', value: string) => void;
  onPresetClick: (field: 'competencia' | 'diferencial', presetLabel: string) => void;
  onCustomSkillClick: (field: 'competencia' | 'diferencial') => void;
  onSkillRemove: (field: 'competencia' | 'diferencial', skillId: string) => void;
  onDiversityToggle: (id: DiversityCriterionId) => void;
  onBack: () => void;
  onSubmit: () => void;
}

const DIVERSITY_OPTIONS: readonly { id: DiversityCriterionId; label: string }[] = [
  { id: 'mulheres', label: 'Mulheres (CIS ou Trans)' },
  { id: 'mulheres-negras', label: 'Mulheres negras' },
  { id: 'transexuais', label: 'Pessoas transexuais' },
  { id: 'lgbtqiap', label: 'Pessoas LGBTQIAPN+' },
  { id: 'amarelas', label: 'Pessoas amarelas' },
  { id: 'indigenas', label: 'Pessoas indígenas' },
  { id: 'pardas', label: 'Pessoas pardas' },
  { id: 'pretas', label: 'Pessoas pretas' },
  { id: 'pessoas-com-deficiencia', label: 'Pessoas com deficiência' },
  { id: '50-plus', label: 'Pessoas 50+' },
];

export function JobEditRequirementsPage({
  jobName,
  companyLogoUrl,
  companyName,
  values,
  errors,
  isSubmitting,
  canSubmit,
  readOnly = false,
  competenciaPresets,
  diferencialPresets,
  competenciaCapError,
  diferencialCapError,
  onAgeChange,
  onPresetClick,
  onCustomSkillClick,
  onSkillRemove,
  onDiversityToggle,
  onBack,
  onSubmit,
}: JobEditRequirementsPageProps) {
  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="jobs" />

      <main className="flex flex-1 flex-col items-center gap-8 bg-card px-4 pt-8 pb-16 md:px-8">
        <form
          className="contents"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <header className="flex w-full max-w-[630px] flex-col gap-2">
            <div className="flex flex-row items-center gap-2">
              <JobHeaderLogo logoUrl={companyLogoUrl} companyName={companyName} />
              <h1 className="text-3xl font-medium leading-none tracking-tight text-foreground md:text-4xl">
                {jobName}
              </h1>
            </div>
            <p className="text-xl font-medium leading-none tracking-tight text-accent-foreground md:text-2xl">
              Requisitos e diferenciais
            </p>
          </header>

          <div className="flex w-full max-w-[630px] flex-1 flex-col gap-5">
            {/* EU-569 — fieldset nativo desabilita os controles em modo
                visualização (inputs de idade, pills, checkboxes). */}
            <fieldset
              disabled={readOnly}
              className="contents"
              data-testid={readOnly ? 'job-edit-readonly-fieldset' : undefined}
            >
              <BlockedEditTooltip enabled={readOnly}>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <FormField error={Boolean(errors.ageMin)}>
                    <FormFieldLabel className="text-foreground">Idade mínima</FormFieldLabel>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={14}
                      value={values.ageMin}
                      placeholder="Ex.: 18"
                      variant={errors.ageMin ? 'destructive' : 'default'}
                      onChange={(event) => onAgeChange('ageMin', event.target.value)}
                      data-testid="job-requirements-age-min"
                    />
                    <FormFieldError className="text-sm leading-5 text-muted-foreground">
                      {errors.ageMin}
                    </FormFieldError>
                  </FormField>
                  <FormField error={Boolean(errors.ageMax)}>
                    <FormFieldLabel className="text-foreground">Idade máxima</FormFieldLabel>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={14}
                      value={values.ageMax}
                      placeholder="Ex.: 24"
                      variant={errors.ageMax ? 'destructive' : 'default'}
                      onChange={(event) => onAgeChange('ageMax', event.target.value)}
                      data-testid="job-requirements-age-max"
                    />
                    <FormFieldError className="text-sm leading-5 text-muted-foreground">
                      {errors.ageMax}
                    </FormFieldError>
                  </FormField>
                </div>
              </BlockedEditTooltip>

              <BlockedEditTooltip enabled={readOnly}>
                <FormField error={Boolean(competenciaCapError)}>
                  <FormFieldLabel className="text-foreground">Competências</FormFieldLabel>
                  <SkillPillGroup
                    presets={competenciaPresets}
                    selected={values.selectedCompetencias}
                    onPresetClick={(label) => onPresetClick('competencia', label)}
                    onSkillRemove={(id) => onSkillRemove('competencia', id)}
                    onAddNew={() => onCustomSkillClick('competencia')}
                    testIdPrefix="job-requirements-competencia"
                  />
                  {competenciaCapError ? (
                    <FormFieldError
                      className="text-sm leading-5 text-destructive"
                      data-testid="job-requirements-competencia-cap-error"
                    >
                      {competenciaCapError}
                    </FormFieldError>
                  ) : (
                    <FormFieldHint>
                      Conhecimentos e habilidades essenciais para a vaga
                    </FormFieldHint>
                  )}
                </FormField>
              </BlockedEditTooltip>

              <BlockedEditTooltip enabled={readOnly}>
                <FormField error={Boolean(diferencialCapError)}>
                  <FormFieldLabel className="text-foreground">Diferenciais</FormFieldLabel>
                  <SkillPillGroup
                    presets={diferencialPresets}
                    selected={values.selectedDiferenciais}
                    onPresetClick={(label) => onPresetClick('diferencial', label)}
                    onSkillRemove={(id) => onSkillRemove('diferencial', id)}
                    onAddNew={() => onCustomSkillClick('diferencial')}
                    testIdPrefix="job-requirements-diferencial"
                  />
                  {diferencialCapError ? (
                    <FormFieldError
                      className="text-sm leading-5 text-destructive"
                      data-testid="job-requirements-diferencial-cap-error"
                    >
                      {diferencialCapError}
                    </FormFieldError>
                  ) : (
                    <FormFieldHint>Habilidades extras que destacam o candidato</FormFieldHint>
                  )}
                </FormField>
              </BlockedEditTooltip>

              <BlockedEditTooltip enabled={readOnly}>
                <FormField>
                  <FormFieldLabel className="text-foreground">
                    Critérios de diversidade
                  </FormFieldLabel>
                  <div className="flex flex-row flex-wrap gap-2">
                    {DIVERSITY_OPTIONS.map((option) => {
                      const isChecked = values.selectedDiversity.includes(option.id);
                      const isDisability = option.id === 'pessoas-com-deficiencia';
                      const displayLabel =
                        isDisability && values.disabilityDetails
                          ? values.disabilityDetails.label
                          : option.label;
                      return (
                        <label
                          key={option.id}
                          className={cn(
                            'group inline-flex cursor-pointer select-none items-center gap-2 rounded-xl border bg-card px-4 py-3 text-sm font-medium transition-colors',
                            isChecked
                              ? 'border-primary bg-primary/10 text-foreground'
                              : 'border-border text-foreground hover:border-primary'
                          )}
                        >
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={() => onDiversityToggle(option.id)}
                            className="size-4"
                            data-testid={`job-requirements-diversity-${option.id}`}
                          />
                          <span>{displayLabel}</span>
                        </label>
                      );
                    })}
                  </div>
                </FormField>
              </BlockedEditTooltip>
            </fieldset>

            <div className="mt-auto flex flex-row justify-between gap-2 pt-8">
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={onBack}
                data-testid="job-requirements-back"
              >
                Voltar
              </Button>
              {readOnly ? (
                <Button type="button" size="lg" disabled data-testid="job-edit-save-blocked">
                  Salvar alterações
                </Button>
              ) : (
                <Button
                  type="submit"
                  size="lg"
                  loading={isSubmitting}
                  disabled={!canSubmit || isSubmitting}
                  data-testid="job-requirements-submit"
                >
                  Salvar alterações
                </Button>
              )}
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}

interface SkillPillGroupProps {
  presets: readonly string[];
  selected: readonly SelectedSkill[];
  onPresetClick: (label: string) => void;
  onSkillRemove: (id: string) => void;
  onAddNew: () => void;
  testIdPrefix: string;
}

function SkillPillGroup({
  presets,
  selected,
  onPresetClick,
  onSkillRemove,
  onAddNew,
  testIdPrefix,
}: SkillPillGroupProps) {
  // Selected skills (with their level suffix) replace the matching preset pill.
  const selectedByName = new Map(selected.map((skill) => [skill.name.toLowerCase(), skill]));

  return (
    <div className="flex flex-row flex-wrap gap-2">
      {presets.map((preset) => {
        const match = selectedByName.get(preset.toLowerCase());
        if (match) {
          return (
            <SkillPill
              key={`preset-${preset}`}
              label={match.label}
              checked
              onClick={() => onSkillRemove(match.id)}
              testId={`${testIdPrefix}-preset-${slugify(preset)}`}
            />
          );
        }
        return (
          <SkillPill
            key={`preset-${preset}`}
            label={preset}
            checked={false}
            onClick={() => onPresetClick(preset)}
            testId={`${testIdPrefix}-preset-${slugify(preset)}`}
          />
        );
      })}
      {selected
        .filter(
          (skill) => !presets.some((preset) => preset.toLowerCase() === skill.name.toLowerCase())
        )
        .map((skill) => (
          <SkillPill
            key={skill.id}
            label={skill.label}
            checked
            onClick={() => onSkillRemove(skill.id)}
            testId={`${testIdPrefix}-custom-${slugify(skill.id)}`}
          />
        ))}
      <Button
        type="button"
        variant="secondary"
        size="lg"
        onClick={onAddNew}
        className="self-start"
        data-testid={`${testIdPrefix}-add`}
      >
        Adicionar novo
        <PlusIcon className="size-4" />
      </Button>
    </div>
  );
}

interface SkillPillProps {
  label: string;
  checked: boolean;
  onClick: () => void;
  testId: string;
}

function SkillPill({ label, checked, onClick, testId }: SkillPillProps) {
  return (
    <label
      className={cn(
        'group inline-flex cursor-pointer select-none items-center gap-2 rounded-xl border bg-card px-4 py-3 text-sm font-medium transition-colors',
        checked
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border text-foreground hover:border-primary'
      )}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={onClick}
        className="size-4"
        data-testid={testId}
      />
      <span>{label}</span>
    </label>
  );
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
